"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  type getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from "firebase/auth"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { defaultFormValidation } from "@/lib/actions/formProps"
import { NO_ORGANIZATION_ERROR_CODE } from "@/lib/auth/NoOrganizationError"
import { resolveLandingDestination } from "@/lib/organization/resolveLandingDestination"
import { loginAction } from "../actions/login"
import { trackLogIn } from "../analytics/events"

type FirebaseAuthError = { code?: string }

const mfaCodeSchema = z.object({ code: z.string().min(1) })
type MfaCodeInput = z.infer<typeof mfaCodeSchema>

export type MfaVerifyFormProps = {
  resolver: ReturnType<typeof getMultiFactorResolver>
  verificationId: string
  phoneHint: string
  redirectTo: string
  onBack: () => void
}

export const MfaVerifyForm = ({
  resolver,
  verificationId,
  phoneHint,
  redirectTo,
  onBack,
}: MfaVerifyFormProps) => {
  const t = useTranslations("auth.mfa")
  const tErrors = useTranslations("auth.login.errors")
  const router = useRouter()

  const form = useForm<MfaCodeInput>({
    resolver: zodResolver(mfaCodeSchema),
    defaultValues: { code: "" },
    ...defaultFormValidation,
  })

  const onSubmit = form.handleSubmit(async ({ code }) => {
    form.clearErrors("root")
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code)
      const assertion = PhoneMultiFactorGenerator.assertion(credential)
      const userCredential = await resolver.resolveSignIn(assertion)
      const idToken = await userCredential.user.getIdToken()
      const result = await loginAction({ idToken })
      if (result?.serverError === NO_ORGANIZATION_ERROR_CODE) {
        form.setError("root.serverError", {
          message: tErrors("noOrganization"),
        })
        return
      }
      if (result?.serverError || result?.validationErrors || !result?.data) {
        form.setError("root.serverError", { message: tErrors("generic") })
        return
      }
      if (!result.data.organizationId) {
        form.setError("root.serverError", {
          message: tErrors("noOrganization"),
        })
        return
      }
      trackLogIn()
      router.replace(
        resolveLandingDestination({
          redirectTo,
          lastVisitedOrgId: result.data.lastVisitedOrgId,
          defaultOrgId: result.data.organizationId,
        }),
      )
    } catch (error) {
      const errorCode = (error as FirebaseAuthError)?.code
      const message =
        errorCode === "auth/invalid-verification-code"
          ? tErrors("mfaCodeInvalid")
          : errorCode === "auth/code-expired"
            ? tErrors("mfaCodeExpired")
            : tErrors("generic")
      form.setError("root.serverError", { message })
    }
  })

  const isSubmitting = form.formState.isSubmitting
  const rootError = form.formState.errors.root?.serverError

  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup>
        <Controller
          name="code"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid || undefined}>
              <FieldLabel htmlFor="mfa-code">{t("code.label")}</FieldLabel>
              <FieldDescription>
                {t("description", { phoneHint })}
              </FieldDescription>
              <Input
                {...field}
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                enterKeyHint="go"
                placeholder={t("code.placeholder")}
                aria-invalid={fieldState.invalid || undefined}
                disabled={isSubmitting}
                autoFocus
              />
              {fieldState.invalid && fieldState.error?.message && (
                <FieldError errors={[{ message: fieldState.error.message }]} />
              )}
            </Field>
          )}
        />
        {rootError?.message && (
          <FieldError errors={[{ message: rootError.message }]} />
        )}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t("submitting") : t("submit")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          disabled={isSubmitting}
          onClick={onBack}
        >
          {t("backToLogin")}
        </Button>
        <FieldDescription className="sr-only">{t("heading")}</FieldDescription>
      </FieldGroup>
    </form>
  )
}
